import React from "react"
import { Link } from "react-router-dom"
import { StarIcon, ShoppingBagIcon } from "@heroicons/react/24/solid"
import { StarIcon as StarOutlineIcon } from "@heroicons/react/24/outline"
import { useRecommendation } from "../contexts/RecommendationContext"

const ProductCard = ({ product, onPurchase }) => {
  const { generateAffiliateLink, savePurchase } = useRecommendation()
  const renderStars = rating => {
    const stars = []
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 !== 0

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(<StarIcon key={i} className="h-4 w-4 text-yellow-400" />)
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative">
            <StarOutlineIcon className="h-4 w-4 text-gray-300" />
            <div className="absolute inset-0 overflow-hidden w-1/2">
              <StarIcon className="h-4 w-4 text-yellow-400" />
            </div>
          </div>,
        )
      } else {
        stars.push(<StarOutlineIcon key={i} className="h-4 w-4 text-gray-300" />)
      }
    }
    return stars
  }

  const handlePurchaseClick = async () => {
    console.log("handlePurchaseClick", product)
    // Use the same logic as ProductDetails handlePurchase
    if (!product) return

    try {
      console.log("generateAffiliateLink - product card", product.asin)
      // Generate affiliate link and track purchase
      const affiliateResult = await generateAffiliateLink(product.asin, product.url || product.product_url)

      if (affiliateResult.success) {
        // Save purchase record
        await savePurchase({
          product_id: product.asin,
          product_name: product.title || product.product_title,
          product_price: product.price || product.product_price,
          amazon_url: product.url || product.product_url,
          affiliate_link: affiliateResult.affiliateLink,
        })

        // Open affiliate link
        window.open(affiliateResult.affiliateLink, "_blank", "noopener,noreferrer")
      } else {
        // Fallback to regular Amazon link
        const amazonUrl = product.url || product.product_url || `https://amazon.com/dp/${product.asin}`
        window.open(amazonUrl, "_blank", "noopener,noreferrer")
      }
    } catch (error) {
      console.error("Purchase error:", error)
      // Fallback to regular Amazon link
      const amazonUrl = product.url || product.product_url || `https://amazon.com/dp/${product.asin}`
      window.open(amazonUrl, "_blank", "noopener,noreferrer")
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
      {/* Product Image */}
      <div className="aspect-w-1 aspect-h-1 w-full">
        <img
          src={product.imageUrl || product.image}
          alt={product.title}
          className="w-full h-48 object-contain pt-4"
          onError={e => {
            e.target.src = "https://picsum.photos/300/300?random=fallback"
          }}
        />
      </div>

      {/* Product Info */}
      <div className="p-4">
        {/* Title */}
        <h3 className="text-sm font-medium text-gray-900 mb-2 line-clamp-2">{product.title}</h3>

        {/* Rating */}
        <div className="flex items-center mb-2">
          <div className="flex items-center">{renderStars(product.rating)}</div>
          <span className="ml-2 text-sm text-gray-600">({product.reviewCount?.toLocaleString()})</span>
        </div>

        {/* Price */}
        <div className="flex items-center mb-3">
          <span className="text-lg font-bold text-gray-900">{product.price}</span>
          {product.originalPrice && product.originalPrice > product.price && (
            <>
              <span className="ml-2 text-sm text-gray-500 line-through">${product.originalPrice}</span>
              {product.discount && <span className="ml-2 text-sm text-green-600 font-medium">-{product.discount}%</span>}
            </>
          )}
        </div>

        {/* Prime Badge */}
        {product.isPrime && (
          <div className="mb-3">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Prime</span>
          </div>
        )}

        {/* Category */}
        <div className="mb-3">
          <span className="text-xs text-gray-500">{product.category}</span>
        </div>

        {/* Actions */}
        <div className="flex space-x-2">
          <Link
            to={`/product/${product.asin || product.id}`}
            className="flex-1 bg-gray-100 text-gray-700 px-3 py-2 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors text-center"
          >
            View Details
          </Link>
          <button
            onClick={handlePurchaseClick}
            className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-md text-sm font-medium hover:bg-purple-700 transition-colors flex items-center justify-center"
          >
            <ShoppingBagIcon className="h-4 w-4 mr-1" />
            Buy Now
          </button>
        </div>

        {/* Shipping Info */}
        {product.shippingInfo && <div className="mt-2 text-xs text-green-600">{product.shippingInfo}</div>}
      </div>
    </div>
  )
}

export default ProductCard
